import { Loader2 } from 'lucide-react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils/cn'
import { FormPicker } from '../form-picker'
import type { DataIoForm, LoadedFormDefinition } from '../../api/forms'
import type { CommitStatus, ImportShape, MappingConfig, RecordIdentityMode } from '../../lib/mapping-types'

const SHAPE_LABEL: Record<ImportShape, { title: string; desc: string }> = {
  single: { title: 'تک‌نمونه', desc: 'هر ردیف = یک رکورد، یک ثبت' },
  repeatable_long: { title: 'تکرارپذیر — بلند', desc: 'ردیف‌های هم‌گروه = یک رکورد با چند نمونه' },
  repeatable_wide: { title: 'تکرارپذیر — عریض', desc: 'هر ردیف = یک رکورد؛ نمونه‌ها ستون‌های تکراری' },
}

const IDENTITY_LABEL: Record<RecordIdentityMode, { title: string; desc: string }> = {
  create_new: { title: 'ساخت رکورد جدید', desc: 'برای هر ورودی یک رکورد تازه' },
  match_display_id: { title: 'تطبیق با شناسهٔ رکورد', desc: 'به‌روزرسانی/افزودن به رکورد موجود بر اساس display_id' },
  match_field: { title: 'تطبیق با یک فیلد', desc: 'یافتن رکورد موجود از روی مقدار یک فیلد' },
}

export function StepTarget({
  projectId,
  headers,
  form,
  def,
  defLoading,
  config,
  commitStatus,
  onForm,
  onConfig,
  onCommitStatus,
}: {
  projectId: string
  headers: string[]
  form: DataIoForm | null
  def: LoadedFormDefinition | null
  defLoading: boolean
  config: MappingConfig
  commitStatus: CommitStatus
  onForm: (f: DataIoForm) => void
  onConfig: (c: MappingConfig) => void
  onCommitStatus: (s: CommitStatus) => void
}) {
  const isRepeatable = form?.instanceType === 'repeatable'
  const shapes: ImportShape[] = isRepeatable ? ['repeatable_long', 'repeatable_wide'] : ['single']

  const set = (patch: Partial<MappingConfig>) => onConfig({ ...config, ...patch })
  const setIdentity = (patch: Partial<MappingConfig['identity']>) =>
    onConfig({ ...config, identity: { ...config.identity, ...patch } })

  const toggleGroupBy = (col: string) => {
    const has = config.identity.groupByColumns.includes(col)
    setIdentity({
      groupByColumns: has
        ? config.identity.groupByColumns.filter((c) => c !== col)
        : [...config.identity.groupByColumns, col],
    })
  }

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-sm font-semibold mb-2">فرم مقصد</h4>
        <FormPicker projectId={projectId} value={form?.id ?? ''} onChange={onForm} />
        {defLoading && (
          <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> بارگذاری تعریف فرم…
          </p>
        )}
        {def && (
          <p className="text-xs text-muted-foreground mt-2">
            نسخهٔ منتشرشده {def.versionLabel} · {def.fields.length} فیلد
          </p>
        )}
      </div>

      {form && def && (
        <>
          <div>
            <h4 className="text-sm font-semibold mb-2">ساختار داده</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {shapes.map((s) => (
                <Opt
                  key={s}
                  active={config.shape === s}
                  onClick={() => set({ shape: s })}
                  title={SHAPE_LABEL[s].title}
                  desc={SHAPE_LABEL[s].desc}
                />
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold mb-2">هویت رکورد</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {(Object.keys(IDENTITY_LABEL) as RecordIdentityMode[]).map((m) => (
                <Opt
                  key={m}
                  active={config.identity.mode === m}
                  onClick={() => setIdentity({ mode: m })}
                  title={IDENTITY_LABEL[m].title}
                  desc={IDENTITY_LABEL[m].desc}
                />
              ))}
            </div>

            {/* Mode-specific column pickers */}
            <div className="mt-3 space-y-3">
              {config.identity.mode === 'match_display_id' && (
                <Labeled label="ستون شناسهٔ رکورد (display_id)">
                  <ColumnSelect headers={headers} value={config.identity.displayIdColumn ?? ''} onChange={(v) => setIdentity({ displayIdColumn: v })} />
                </Labeled>
              )}
              {config.identity.mode === 'match_field' && (
                <div className="grid grid-cols-2 gap-2">
                  <Labeled label="فیلد تطبیق">
                    <Select value={config.identity.matchFieldKey ?? ''} onValueChange={(v) => setIdentity({ matchFieldKey: v })}>
                      <SelectTrigger><SelectValue placeholder="فیلد" /></SelectTrigger>
                      <SelectContent>
                        {def.fields.map((f) => (
                          <SelectItem key={f.field_key} value={f.field_key}>{f.label?.fa || f.field_key}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Labeled>
                  <Labeled label="ستون مقدار">
                    <ColumnSelect headers={headers} value={config.identity.matchColumn ?? ''} onChange={(v) => setIdentity({ matchColumn: v })} />
                  </Labeled>
                </div>
              )}

              {config.shape === 'repeatable_long' && (
                <Labeled label="گروه‌بندی ردیف‌ها در یک رکورد بر اساس ستون‌(های):">
                  <div className="flex flex-wrap gap-2">
                    {headers.map((h) => (
                      <label key={h} className="flex items-center gap-1.5 text-xs border border-border rounded-lg px-2 py-1 cursor-pointer">
                        <Checkbox checked={config.identity.groupByColumns.includes(h)} onCheckedChange={() => toggleGroupBy(h)} />
                        <span className="font-mono">{h}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">بدون انتخاب، هر ردیف یک رکورد جداگانه می‌شود.</p>
                </Labeled>
              )}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold mb-2">وضعیت پس از ورود</h4>
            <div className="grid grid-cols-2 gap-2">
              <Opt active={commitStatus === 'draft'} onClick={() => onCommitStatus('draft')} title="پیش‌نویس" desc="قابل ویرایش، نهایی نشده" />
              <Opt active={commitStatus === 'submitted'} onClick={() => onCommitStatus('submitted')} title="ثبت‌شده" desc="نهایی‌شده برای بررسی" />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground block mb-1">{label}</label>
      {children}
    </div>
  )
}

function ColumnSelect({ headers, value, onChange }: { headers: string[]; value: string; onChange: (v: string) => void }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger><SelectValue placeholder="ستون" /></SelectTrigger>
      <SelectContent>
        {headers.map((h) => (
          <SelectItem key={h} value={h}>{h}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function Opt({ active, onClick, title, desc }: { active: boolean; onClick: () => void; title: string; desc: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-start rounded-lg border p-3 transition-colors',
        active ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border hover:bg-muted/30',
      )}
    >
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
    </button>
  )
}
